import csiAction from "csi/action";
import NewFrom from "csi/action/NewFrom";
import { Action } from "csi/def/Action";
import Parameter from "csi/def/Parameter";
import Request from "csi/def/Request";
import NewFromController from "csi/table/NewFrom";
import { C8NodeType } from "csi/typings";
import declare from "dojo/_base/declare";

// Data Sheet subtypes
const FOOD_BOM = "FoodBOM";
const FOOD_LABEL_DATA_SHEET = "FoodLabelDataSheet";
const SPECIFICATION_DATA_SHEET = "SpecificationDataSheet";
const MATERIAL_BOM = "MaterialBOM";
const TEST_RUN = "TestRun";
const DEFAULT_AC_MODULE = "SiteAdmin";
const FORMULATED_PRODUCT_AC_MODULE = "FormulatedProduct";
const DEFAULT_AC_OPERATION = "NewFromDataSheet";

const NewFromOriginal = declare(NewFromController, {
	datasheetsToCopy: null,

	/**
	 * Process each selected datasheet by constructing action based on datasheet type,
	 * and then suspend this action to route to the derived proxy action.
	 */
	process(): void {
		if (!this.datasheetsToCopy) {
			this.datasheetsToCopy = this.getFormData().getSelectedNodes();
			this.postProcessProxy();
		}

		const nextDatasheet = this.datasheetsToCopy.shift();
		if (nextDatasheet) {
			const action = this._findNewFromActionByType(nextDatasheet.$Type);
			if (action) {
				const { widget, _closeReactDialogHandle } = this;

				this.suspendProcess();

				// summary: arrow functions are executed within context of NewFromOriginal action,
				// and non-arrow functions are executed within context of routed action.
				csiAction.invoke(widget, null, action, {
					_closeReactDialogHandle,
					onStart: function () {
						const fd = this.getFormData();
						const ctx = widget.getContext(nextDatasheet.$URL);
						fd.changeSelection(ctx, true);
					},
					_canNavigate: function () {
						// disable Save & Go since action can create multiple datasheets
						return false;
					},
					onEnd: () => {
						this.resumeProcess();
						this.process();
					},
				});
			} else {
				console.error(`NewFromOriginal: could not find action for type ${nextDatasheet.$Type}`);
				this.process();
			}
		} else {
			this.postProcess();
		}
	},

	_findNewFromActionByType(type: C8NodeType): Action {
		let classController: string;
		let module: string;
		let operation: string;
		let params: Parameter[];
		let req: Request;

		const subTypeParam = Object.assign(new Parameter(), {
			m_id: "Subtype",
			m_typeAttr: "ref",
		});

		// summary: if datasheet type has specified controller class and operation, then return new action
		switch (type) {
			case SPECIFICATION_DATA_SHEET:
				classController = "SiteAdmin/SubType/DisableFieldForNewFromController";
				module = DEFAULT_AC_MODULE;
				operation = DEFAULT_AC_OPERATION;
				break;
			case FOOD_BOM:
				module = FORMULATED_PRODUCT_AC_MODULE;
				operation = "FoodNewFromFoodBOM";
				params = [subTypeParam];
				break;
			case FOOD_LABEL_DATA_SHEET:
				module = FORMULATED_PRODUCT_AC_MODULE;
				operation = "BaseProductNewFromFoodLabelDataSheet";
				params = [subTypeParam];
				break;
			case MATERIAL_BOM:
				classController = "ProductSpec/NewFromMaterialBOMController";
				module = "ProductSpec";
				operation = "MaterialNewFromBOM";
				break;
			case TEST_RUN:
				classController = "Quality/NewFromTestRun";
				module = DEFAULT_AC_MODULE;
				operation = DEFAULT_AC_OPERATION;
				break;
			default:
				module = DEFAULT_AC_MODULE;
				operation = DEFAULT_AC_OPERATION;
				break;
		}

		if (module && operation) {
			req = Object.assign(new Request(), {
				m_module: module,
				m_operation: operation,
				m_parameters: params,
			});
		}

		const ac = Object.assign(new NewFrom(), {
			m_id: `NewFromOriginal${type}`,
			classController,
			m_request: req,
			m_typeNodeHint: type,
		});
		ac.resolve();
		return ac;
	},
});

export = NewFromOriginal;